import { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2, Loader2, Phone, Mail, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function BookingForm() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.phone) {
      setError("Compila tutti i campi obbligatori.");
      return;
    }
    if (!privacyAccepted) {
      setError("Devi accettare la privacy policy per procedere.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      await axios.post(`${API}/bookings`, form);
      setSuccess(true);
      setForm({ name: "", email: "", phone: "", message: "" });
      setPrivacyAccepted(false);
    } catch (err) {
      setError("Errore nell'invio. Riprova tra qualche istante.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contatti" data-testid="booking-section" className="py-24 md:py-32 bg-white">
      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16">
          {/* Left info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <p className="text-xs font-medium text-[#1B4332] uppercase tracking-wider mb-3">Contattaci</p>
            <h2 className="text-3xl md:text-5xl font-semibold tracking-tight mb-6" style={{ fontFamily: 'Outfit', color: '#0F172A' }}>
              Prenota la tua call gratuita
            </h2>
            <p className="text-base md:text-lg text-slate-600 leading-relaxed mb-8">
              Compila il form e ti ricontatteremo entro 24 ore per fissare una call conoscitiva senza impegno.
              Parlaci dei tuoi obiettivi e scopri come possiamo aiutarti.
            </p>

            <div className="space-y-5">
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
                  <Phone size={20} className="text-[#1B4332]" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Telefono</p>
                  <p className="font-medium text-slate-900">320 375 6383</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
                  <Mail size={20} className="text-[#1B4332]" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Email</p>
                  <p className="font-medium text-slate-900">info@colibrimarketingagency.it</p>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="w-11 h-11 rounded-xl bg-green-50 flex items-center justify-center flex-shrink-0">
                  <MapPin size={20} className="text-[#1B4332]" />
                </div>
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wider">Sede</p>
                  <p className="font-medium text-slate-900">Viale Vittorio Veneto 131, Catania</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right form */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {success ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-10 text-center" data-testid="booking-success">
                <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
                  <CheckCircle2 size={32} className="text-emerald-600" />
                </div>
                <h3 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit' }}>
                  Richiesta Inviata!
                </h3>
                <p className="text-slate-600 text-sm">
                  Grazie per averci contattato. Ti ricontatteremo entro 24 ore per fissare la tua call gratuita.
                </p>
                <button
                  onClick={() => setSuccess(false)}
                  className="mt-4 text-[#1B4332] text-sm font-medium hover:underline"
                  data-testid="booking-reset-button"
                >
                  Invia un'altra richiesta
                </button>
              </div>
            ) : (
              <form
                onSubmit={handleSubmit}
                className="bg-slate-50 rounded-2xl p-8 border border-slate-100"
                data-testid="booking-form"
              >
                <div className="space-y-5">
                  <div>
                    <Label htmlFor="name" className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Nome e Cognome *
                    </Label>
                    <Input
                      id="name"
                      name="name"
                      value={form.name}
                      onChange={handleChange}
                      placeholder="Il tuo nome completo"
                      className="bg-white border-slate-200 focus:border-[#52B788] focus:ring-[#52B788]/20 rounded-lg h-12"
                      data-testid="booking-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Email *
                    </Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={form.email}
                      onChange={handleChange}
                      placeholder="la-tua@email.com"
                      className="bg-white border-slate-200 focus:border-[#52B788] focus:ring-[#52B788]/20 rounded-lg h-12"
                      data-testid="booking-email-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone" className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Telefono *
                    </Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={form.phone}
                      onChange={handleChange}
                      placeholder="+39 320 000 0000"
                      className="bg-white border-slate-200 focus:border-[#52B788] focus:ring-[#52B788]/20 rounded-lg h-12"
                      data-testid="booking-phone-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="message" className="text-sm font-medium text-slate-700 mb-1.5 block">
                      Messaggio
                    </Label>
                    <Textarea
                      id="message"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Parlaci del tuo progetto e dei tuoi obiettivi..."
                      rows={4}
                      className="bg-white border-slate-200 focus:border-[#52B788] focus:ring-[#52B788]/20 rounded-lg resize-none"
                      data-testid="booking-message-input"
                    />
                  </div>

                  {/* Privacy checkbox */}
                  <div className="flex items-start gap-3 relative z-10">
                    <Checkbox
                      id="privacy"
                      checked={privacyAccepted}
                      onCheckedChange={(checked) => {
                        setPrivacyAccepted(checked);
                        setError("");
                      }}
                      className="mt-0.5 data-[state=checked]:bg-[#1B4332] data-[state=checked]:border-[#1B4332] relative z-20"
                      data-testid="booking-privacy-checkbox"
                    />
                    <Label htmlFor="privacy" className="text-xs text-slate-500 leading-relaxed cursor-pointer">
                      Acconsento al trattamento dei miei dati personali come da{" "}
                      <a
                        href="https://colibrimarketingagency.it/policy-privacy/"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-[#1B4332] font-medium underline hover:text-[#52B788]"
                        data-testid="privacy-policy-link"
                      >
                        Policy Privacy
                      </a>{" "}
                      aziendale. *
                    </Label>
                  </div>

                  {error && (
                    <p className="text-red-500 text-sm" data-testid="booking-error">{error}</p>
                  )}

                  <Button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-[#1B4332] hover:bg-[#143728] text-white rounded-full h-12 font-medium shadow-lg shadow-green-900/20 text-base"
                    data-testid="booking-submit-button"
                  >
                    {loading ? (
                      <>
                        <Loader2 size={18} className="mr-2 animate-spin" />
                        Invio in corso...
                      </>
                    ) : (
                      <>
                        <Send size={18} className="mr-2" />
                        Prenota la Tua Call Gratuita
                      </>
                    )}
                  </Button>
                </div>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
